/**
 * Where everything on the timeline is drawn.
 *
 * Everything: the window, the ticks, the order of the rows, every bar's two percentages,
 * every note's text and position. The components do nothing but draw what comes out of
 * here, which is what makes the picture testable without a browser — and the picture is
 * arithmetic, so it is exactly the part worth testing.
 */

import type {
  TimelineBooking,
  TimelineDriver,
  TimelineOffer,
  TimelineOrder,
  TimelineSnapshot,
} from './api';

const MINUTE = 60_000;

/** A tone the timeline's `Bar` primitive understands. */
export type BarTone = 'ok' | 'warn' | 'danger' | 'brand' | 'break';

export interface Span {
  from: number;
  to: number;
  tone: BarTone;
  dashed?: boolean;
  hollow?: boolean;
}

export interface TimelineWindow {
  start: number;
  end: number;
  now: number;
  /** Where `now` sits, as a percentage of the track. */
  nowPct: number;
  /** The quarter hours inside the window, as percentages and as moments. */
  ticks: { pct: number; at: number }[];
  /** A moment as a percentage of the track, clamped to the window. */
  pct: (at: number | string) => number;
}

export function timelineWindow(snapshot: TimelineSnapshot): TimelineWindow {
  const start = Date.parse(snapshot.windowStart);
  const end = Date.parse(snapshot.windowEnd);
  const now = Date.parse(snapshot.asOf);
  const width = Math.max(end - start, 1);

  const pct = (at: number | string) => {
    const moment = typeof at === 'number' ? at : Date.parse(at);

    // Clamped rather than allowed off the end: a bar that started an hour before the window
    // is drawn from its left edge, which is the honest picture of "already running".
    return Math.min(100, Math.max(0, ((moment - start) / width) * 100));
  };

  const ticks: { pct: number; at: number }[] = [];
  const first = Math.ceil(start / (15 * MINUTE)) * (15 * MINUTE);

  for (let at = first; at <= end; at += 15 * MINUTE) ticks.push({ pct: pct(at), at });

  return { start, end, now, nowPct: pct(now), ticks, pct };
}

/** Whether a stretch of time touches the window at all. */
function visible(window: TimelineWindow, from: number, to: number): boolean {
  return to >= window.start && from <= window.end;
}

/** Seconds an offer has left to be answered, never negative. */
export function secondsLeft(offer: TimelineOffer, now: number): number {
  return Math.max(0, Math.round((Date.parse(offer.expiresAt) - now) / 1000));
}

/**
 * The moment a driver's current job is expected to free them.
 *
 * Used for the ordering as well as for the bar, so it lives here rather than being worked
 * out twice.
 */
function freeAt(driver: TimelineDriver): number {
  return driver.job === null ? 0 : Date.parse(driver.job.expectedEndAt);
}

export interface DriverRow {
  driver: TimelineDriver;
  /** What they are on. Empty for a free driver, which is the whole point of the view. */
  bars: (Span & { label: string; orderId?: string; title?: string })[];
  /** What there is to say instead, or beside, the bars. */
  notes: { at: number; text: string; tone?: 'brand' | 'warn' | 'neutral' }[];
  /** The end of the shift, when the cap falls inside the window. */
  marker: { at: number; at_: number } | null;
  /** Where the row sorts: busy first by when they come free, then longest free, then breaks. */
  kind: 'busy' | 'free' | 'break';
}

/**
 * One row per driver, in the order the design asks for: busy drivers first by when they come
 * free, then free drivers longest-free first, then breaks.
 *
 * That order is the view's answer to the question the queue cannot answer — not who is free
 * now, but who will be free in eight minutes. Reading down the rows is reading forward in
 * time.
 */
export function driverRows(snapshot: TimelineSnapshot): DriverRow[] {
  const window = timelineWindow(snapshot);
  const rows = snapshot.drivers.map((driver) => row(driver, window));

  const rank = { busy: 0, free: 1, break: 2 };

  return rows.sort((a, b) => {
    if (rank[a.kind] !== rank[b.kind]) return rank[a.kind] - rank[b.kind];

    if (a.kind === 'busy') return freeAt(a.driver) - freeAt(b.driver);

    // Longest free first: the driver whose state has been Available the longest.
    return Date.parse(a.driver.shiftStateSince) - Date.parse(b.driver.shiftStateSince);
  });
}

function row(driver: TimelineDriver, window: TimelineWindow): DriverRow {
  const bars: DriverRow['bars'] = [];
  const notes: DriverRow['notes'] = [];

  /**
   * A hand-placed offer arrives as a job *and* an offer on the same driver, for the same
   * order: `AssignAsync` moves the order to Assigned and sets its driver before writing the
   * pending row. Nothing is actually running until the driver answers, so the offer is the
   * only true thing to draw — one order, one bar, with the countdown on it. A broadcast is
   * the other case, and there the offer stands alone with the order still waiting.
   */
  const unanswered =
    driver.offer !== null && driver.job !== null && driver.offer.orderId === driver.job.orderId;

  const kind: DriverRow['kind'] =
    driver.shiftState === 'OnBreak' ? 'break' : driver.job !== null && !unanswered ? 'busy' : 'free';

  if (driver.job !== null && !unanswered) {
    const job = driver.job;
    const end = Date.parse(job.expectedEndAt);

    // The leg to the pickup, while it has not happened yet: an outline rather than a fill,
    // because nothing has been earned during it.
    if (
      (job.state === 'Assigned' || job.state === 'EnRouteToPickup') &&
      job.expectedPickupAt !== null
    ) {
      const from = Date.parse(job.startedAt);
      const to = Date.parse(job.expectedPickupAt);

      if (visible(window, from, to)) {
        bars.push({
          from: window.pct(from),
          to: window.pct(to),
          tone: 'ok',
          dashed: true,
          hollow: true,
          label: '',
          orderId: job.orderId,
          title: job.pickupAddress,
        });
      }
    }

    // Waiting at the pickup for somebody who has not come out yet.
    if (job.state === 'WaitingForRider' && job.arrivedAt !== null) {
      const from = Date.parse(job.arrivedAt);

      if (visible(window, from, window.now)) {
        bars.push({
          from: window.pct(from),
          to: window.pct(window.now),
          tone: 'warn',
          label: job.reference,
          orderId: job.orderId,
          title: job.pickupAddress,
        });
      }
    }

    // The ride itself, from whichever pickup moment is known to the expected end.
    const rideFrom = Date.parse(
      job.pickedUpAt ?? job.arrivedAt ?? job.expectedPickupAt ?? job.startedAt,
    );

    if (visible(window, rideFrom, end)) {
      bars.push({
        from: window.pct(rideFrom),
        to: window.pct(end),
        tone: 'ok',
        label: job.reference,
        orderId: job.orderId,
        title: job.destinationAddress ?? job.pickupAddress,
      });
    }

    if (end >= window.start && end <= window.end) {
      notes.push({ at: window.pct(end), text: 'freeAt', tone: 'neutral' });
    }
  }

  // An offer is read from the assignment rather than from the driver's state, because a
  // broadcast leaves the order Requested and the driver Available with a pending row each.
  if (driver.offer !== null) {
    const from = Date.parse(driver.offer.offeredAt);
    const to = Date.parse(driver.offer.expiresAt);

    if (visible(window, from, to)) {
      bars.push({
        from: window.pct(from),
        to: window.pct(to),
        tone: 'brand',
        dashed: true,
        label: driver.offer.reference,
        orderId: driver.offer.orderId,
      });
    }
  }

  if (driver.shiftState === 'OnBreak') {
    const from = Date.parse(driver.shiftStateSince);

    if (visible(window, from, window.now)) {
      bars.push({
        from: window.pct(from),
        to: window.pct(window.now),
        tone: 'break',
        label: 'break',
      });
    }
  }

  // A free driver has no bar at all — only a note, which is what makes an empty row read as
  // spare capacity rather than as missing data. Somebody with an offer out is not free.
  if (kind === 'free' && driver.offer === null) {
    notes.push({ at: window.pct(driver.shiftStateSince), text: 'freeSince', tone: 'neutral' });
  }

  const cap = Date.parse(driver.shiftCapAt);

  return {
    driver,
    bars,
    notes,
    marker: cap >= window.start && cap <= window.end ? { at: window.pct(cap), at_: cap } : null,
    kind,
  };
}

export interface WaitingBar extends Span {
  order: TimelineOrder;
  /** From when it was asked for — or from when it fell due — to now. */
  since: number;
}

/**
 * Everything without a driver, as a bar from the moment it was asked for to now.
 *
 * `danger` when the automation has given up or a booking is nearly due with nobody going,
 * `warn` otherwise. The note beside it says what the automation is doing, which is what a
 * dispatcher needs before deciding to take it over.
 */
export function waitingLane(snapshot: TimelineSnapshot): WaitingBar[] {
  const window = timelineWindow(snapshot);

  return snapshot.waiting.map((order) => {
    const since = Date.parse(order.scheduledFor ?? order.requestedAt);

    return {
      order,
      since,
      from: window.pct(since),
      to: window.pct(window.now),
      tone: order.needsAttention ? 'danger' : 'warn',
    };
  });
}

export interface BookedBar {
  booking: TimelineBooking;
  /** The dotted run-up: from when the search starts to the pickup. */
  lead: { from: number; to: number };
  /** The solid five minutes at the pickup itself. */
  bar: Span;
}

/**
 * The pre-bookings whose pickup falls in the window, plus how many are beyond it.
 *
 * The five-minute bar is arbitrary and deliberate: a pickup is a moment, and a moment two
 * hours wide is one pixel nobody can click.
 */
export function bookedLane(snapshot: TimelineSnapshot): {
  bars: BookedBar[];
  beyond: number;
} {
  const window = timelineWindow(snapshot);

  const bars = snapshot.booked.map((booking) => {
    const at = Date.parse(booking.scheduledFor);
    const search = Date.parse(booking.searchStartsAt);

    return {
      booking,
      lead: { from: window.pct(search), to: window.pct(at) },
      bar: {
        from: window.pct(at),
        to: window.pct(at + 5 * MINUTE),
        tone: (booking.needsAttention ? 'danger' : 'brand') as BarTone,
      },
    };
  });

  return { bars, beyond: snapshot.totals.bookedBeyondWindow };
}

/** The five figures along the bottom, in the order the design lists them. */
export function strip(snapshot: TimelineSnapshot): { key: string; value: number }[] {
  return [
    { key: 'freeNow', value: snapshot.totals.freeNow },
    { key: 'freeWithin15Min', value: snapshot.totals.freeWithin15Min },
    { key: 'waitingOver10Min', value: snapshot.totals.waitingOver10Min },
    { key: 'bookedWithinHour', value: snapshot.totals.bookedWithinHour },
    { key: 'shiftsEndingWithinHour', value: snapshot.totals.shiftsEndingWithinHour },
  ];
}
